<script setup>
    import {onMounted, reactive, ref} from "vue";
    import ChartDataLabels from 'chartjs-plugin-datalabels';
    import Chart from 'chart.js/auto'
    import {useIntersectionObserver} from "../../useIntersectionObserver";
    Chart.register(ChartDataLabels)

    const props = defineProps({
        data: {
            type: Object,
            required: false,
            default: {}
        },
        chartType: {
            type: Object,
            required: true
        }
    })

    const
        formattedData = ref(null),
        canvasEl = ref(null),
        observedElement = ref(null),
        isInView = useIntersectionObserver(observedElement);

    const chartOptions = ref({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: 'top',
                fullSize: true,
                labels: {
                    boxWidth: 16,
                    boxHeight: 16,
                    usePointStyle: true,
                    pointStyle: 'circle',
                    color: '#0d0d0d',
                    padding: 15,
                    font: {
                        family: 'roboto, sans-serif',
                        size: 13
                    }
                }
            },
            datalabels: {
                display: false
            },
        },
        interaction: {
            mode: 'index',
            intersect: false
        },
        tooltip: {
            enabled: true
        },
        scales: {
            x: {
                stacked: props?.chartType?.value === 'stacked',
                grid: {
                    color: '#464646', // White grid lines on x-axis
                    lineWidth: 0.3
                },
                border: {
                    color: '#0d0d0d', // White border for x-axis
                    width: 0.5
                },
                ticks: {
                    color: '#0d0d0d', // White color for x-axis labels
                    font: {
                        family: 'roboto, sans-serif',
                        size: 10
                    }
                }
            },
            y: {
                stacked: props?.chartType?.value === 'stacked',
                grid: {
                    color: '#464646', // White grid lines on y-axis
                    lineWidth: 0.3
                },
                border: {
                    color: '#0d0d0d', // White border for y-axis
                    width: 0.5
                },
                ticks: {
                    color: '#0d0d0d', // White color for y-axis labels
                    font: {
                        family: 'roboto, sans-serif',
                        size: 10
                    },
                    callback: function (value) {
                        return props.percentage ? value + '%' : value;
                    }
                }
            },
        }
    })

    if (props.percentage) {
        chartOptions.value.scales.y = {
            display: false,
            beginAtZero: true,
            max: 100,
            min: 0,
            ticks: {
                autoSkip: false,
                stepSize: 10,
                callback: function (value) {
                    return (value / 100 * 100).toFixed(0) + '%'; // convert it to percentage
                }
            }
        }
    }

    onMounted(() => {
        const chartData = {
            labels: [], // x-axis labels for each bar
            datasets: []
        }

        props.data.forEach((item, index) => {
            chartData.labels = item?.data.map(data => data.data_label)
            const set = {
                label: item?.set_label,
                data: item?.data.map(data => data.data_value),
                fill: false,
                borderColor: item?.set_colour,
                backgroundColor: item?.set_colour,
                tension: 0.1
            }

            chartData.datasets.push(set)
        })

        formattedData.value = chartData

        new Chart(
            canvasEl.value,
            {
                type: props?.chartType?.value === 'stacked' ? 'bar' : props.chartType.value,
                data: formattedData.value,
                options: chartOptions.value,
                // plugins: [ChartDataLabels]
            }
        );
    })
</script>

<template>
    <div
        ref="observedElement"
        class="w-full h-full transition-all duration-700 delay-300"
        :class="{
            'opacity-0 translate-y-1/3': !isInView
        }"
    >
        <canvas class="w-full h-full" ref="canvasEl" />
    </div>
</template>
